import tokenService from './token-service';
import logger from '../helpers/logger';
import { extractRolesFromToken } from '../utils/jwt-decoder';
import { authRequest } from '../authConfig';

class RoleService {
    constructor() {
        this.cachedRoles = null;
        this.tokenChangeListener = null;
    }

    /**
     * Get user roles from the JWT token
     * @returns {string[]} Array of role names
     */
    async getUserRoles(msalInstance, account) {
        // Check cache first (use explicit null check to correctly handle empty arrays)
        if (this.cachedRoles !== null) {
            return this.cachedRoles;
        }

        let token = tokenService.getToken();
        
        // Acquire token if missing
        if (!token) {
            const tokenResponse = await msalInstance.acquireTokenSilent({
                ...authRequest,
                account,
            });

            token = tokenResponse.accessToken;
            tokenService.setToken(token);
        }

        const roles = extractRolesFromToken(token);
        this.cachedRoles = roles;

        logger.log('User roles extracted:', roles);
        return roles;
    }

    /**
     * Check if user has a specific role (synchronous, uses cached roles)
     * @param {string} role - Role name to check
     * @returns {boolean}
     */
    hasRole(role) {
        const roles = this.cachedRoles || [];
        return roles.includes(role);
    }

    /**
     * Check if user has any of the specified roles (synchronous, uses cached roles)
     * @param {string[]} roles - Array of role names
     * @returns {boolean}
     */
    hasAnyRole(roles) {
        const userRoles = this.cachedRoles || [];
        return roles.some(role => userRoles.includes(role));
    }

    /**
     * Check if user has all of the specified roles (synchronous, uses cached roles)
     * @param {string[]} roles - Array of role names
     * @returns {boolean}
     */
    hasAllRoles(roles) {
        const userRoles = this.cachedRoles || [];
        return roles.every(role => userRoles.includes(role));
    }

    /**
     * Clear cached roles (call this when token changes)
     */
    clearCache() {
        this.cachedRoles = null;
    }

    /**
     * Refresh roles from token (use after token refresh)
     * @param {object} msalInstance - MSAL instance
     * @param {object} account - User account
     * @returns {Promise<string[]>}
     */
    async refreshRoles(msalInstance, account) {
        this.clearCache();
        return this.getUserRoles(msalInstance, account);
    }
}

export default new RoleService();